import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IFirestoreMessage } from '../../../../Types/firestoreTypes';
import { FirestoreService } from '../../../../Services/FirestoreService';
import { getUserFromLS } from '../../../../utils/getUserFromLS';
import { toDateFromFirestore } from '../../../../utils/formatters';
import { useWorkspaceClient } from '../../../../context/WorkspaceClientContext';
import { useFirebaseAuthReady } from '../../../../Services/FirebaseAuthService';

const MAX_RETRIES = 3;
const BASE_RETRY_MS = 2_000;

type MessageWithId = { id: string; data: IFirestoreMessage };

interface ClientMessageContextType {
  messages: MessageWithId[];
  visibleMessages: MessageWithId[];
  isLoading: boolean;
  error: string | null;
  dismissMessage: (messageId: string) => void;
  isMessageDismissed: (messageId: string) => boolean;
}

const ClientMessageContext = createContext<ClientMessageContextType | undefined>(undefined);

interface ClientMessageProviderProps {
  children: ReactNode;
}

export const ClientMessageProvider: React.FC<ClientMessageProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<MessageWithId[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissedMessages, setDismissedMessages] = useState<string[]>([]);
  const { isAuthenticated } = useSelector((state: any) => state.auth);
  
  const user = useMemo(() => getUserFromLS(), [isAuthenticated]);
  const { effectiveClientId } = useWorkspaceClient();
  const isFirebaseReady = useFirebaseAuthReady(user?.id);

  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  // Load dismissed messages from localStorage on mount
  useEffect(() => {
    try {
      const dismissed = JSON.parse(localStorage.getItem('dismissedClientMessages') || '[]');
      setDismissedMessages(dismissed);
    } catch {
      setDismissedMessages([]);
    }
  }, []);

  const subscribe = useCallback((clientId: string) => {
    unsubRef.current?.();

    const handleUpdate = (newMessages: MessageWithId[]) => {
      retryCountRef.current = 0;
      setMessages(newMessages);
      setError(null);
    };

    const handleError = (err: Error) => {
      console.error('Error in client message listener:', err);
      const isPermissionDenied = (err as any).code === 'permission-denied';

      if (isPermissionDenied && retryCountRef.current < MAX_RETRIES) {
        const delay = BASE_RETRY_MS * 2 ** retryCountRef.current;
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(() => subscribe(clientId), delay);
      } else {
        setError(err.message);
      }
    };

    unsubRef.current = FirestoreService.startListeningToClientMessages(
      clientId,
      handleUpdate,
      handleError
    );
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !effectiveClientId || !isFirebaseReady) {
      setMessages([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    retryCountRef.current = 0;
    subscribe(String(effectiveClientId));
    setIsLoading(false);

    return () => {
      unsubRef.current?.();
      unsubRef.current = null;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryCountRef.current = 0;
    };
  }, [isAuthenticated, effectiveClientId, isFirebaseReady, subscribe]);

  // Filter messages that should be visible (enabled, not dismissed, within time range, role-based, etc.)
  const visibleMessages = useMemo(() => {
    return messages.filter(message => {
      // Safety check for message data structure
      if (!message?.data) return false;
      
      if (!message.data.enabled) return false;
      if (dismissedMessages.includes(message.id) && message.data.dismissible) return false;
      
      const now = new Date();
      const startTime = toDateFromFirestore(message.data.startTime);
      const endTime = toDateFromFirestore(message.data.endTime);
      
      if (!startTime || now < startTime) return false;
      if (endTime && now > endTime) return false;
      
      // Role-based filtering
      if (message.data.applicableRoles && message.data.applicableRoles.length > 0) {
        if (!user?.role || !message.data.applicableRoles.includes(user.role)) {
          return false;
        }
      }
      
      return true;
    });
  }, [messages, dismissedMessages, user?.role]);

  // Dismiss a message
  const dismissMessage = useCallback((messageId: string) => {
    setDismissedMessages(prev => {
      const newDismissed = [...prev, messageId];
      // Update localStorage
      try {
        localStorage.setItem('dismissedClientMessages', JSON.stringify(newDismissed));
      } catch {}
      return newDismissed;
    });
  }, []);

  // Check if a message is dismissed
  const isMessageDismissed = useCallback((messageId: string) => {
    return dismissedMessages.includes(messageId);
  }, [dismissedMessages]);

  const value: ClientMessageContextType = {
    messages,
    visibleMessages,
    isLoading,
    error,
    dismissMessage,
    isMessageDismissed,
  };

  return (
    <ClientMessageContext.Provider value={value}>
      {children}
    </ClientMessageContext.Provider>
  );
};

export const useClientMessages = (): ClientMessageContextType => {
  const context = useContext(ClientMessageContext);
  if (context === undefined) {
    throw new Error('useClientMessages must be used within a ClientMessageProvider');
  }
  return context;
}; 